import React, { useCallback, useContext, useState } from 'react';
import TermsAndConditionContext from './TermsAndConditionContext';
import termConditionModel from '../../model/setting/termAndCondition.model'; // You can create a model similar to Service.model
import { useLoading } from '../../utils/LoadingUtil';
import axiosInstance from '../../utils/axiosInstance';
import { ConfirmAlert, showAlert } from '../../components';
import validateForm from '../../utils/validateForm';
import { handleApiError } from '@/lib/handleApiError';

const TermsAndConditionContextProvider = ({ children }) => {
  const { terms, setTerms, initialState } = termConditionModel();
  const { isLoading, startLoading, stopLoading } = useLoading();
  const [error, setError] = useState(null);
  const [termsData, setTermsData] = useState([]);

  const isValidData = useCallback(
    (terms) => {
      const errMsg = {
        termCondition: 'Terms and Conditions are required',
      };

      const isValid = validateForm(terms, setError, errMsg);
      if (!isValid) {
        stopLoading();
        return false;
      }

      return true;
    },
    [stopLoading],
  );

  const getAllTermsAndConditions = useCallback(async () => {
    startLoading();
    try {
      const response = await axiosInstance.get(
        '/SmartProAPI/api/terms-and-conditions',
      );
      setTermsData(response.data);
    } catch (error) {
      handleApiError('error', error.response?.data, error?.response?.status);
    } finally {
      stopLoading();
    }
  }, [startLoading, stopLoading]);

  const deleteTermsAndConditions = useCallback(
    async (id) => {
      const result = await ConfirmAlert({ confirmBtnText: 'Delete Terms' });
      if (!result.isConfirmed) {
        showAlert('error', 'Terms deletion cancelled');
        return;
      }
      startLoading();
      try {
        await axiosInstance.delete(
          `/SmartProAPI/api/terms-and-conditions/${id}`,
        );
        showAlert('success', 'Terms deleted successfully');
        getAllTermsAndConditions();
      } catch (error) {
        handleApiError('error', error.response?.data, error?.response?.status);
        console.log(error);
      } finally {
        stopLoading();
      }
    },
    [getAllTermsAndConditions, startLoading, stopLoading],
  );

  const createNewTermsAndConditions = useCallback(
    async (terms, showTermsScreen) => {
      console.log('api');
      startLoading();
      if (!isValidData(terms)) return;
      console.log('first');
      try {
        await axiosInstance.post(
          '/SmartProAPI/api/terms-and-conditions',
          terms,
        );
        console.log('first');
        showAlert('success', 'Terms added successfully');
        clearFormData();
        showTermsScreen();
        getAllTermsAndConditions();
      } catch (error) {
        setError(error);
        handleApiError('error', error.response?.data, error?.response?.status);
      } finally {
        stopLoading();
      }
    },
    [getAllTermsAndConditions, isValidData, startLoading, stopLoading],
  );

  const updateTermsAndConditions = useCallback(
    async (terms, id, showTermsScreen) => {
      startLoading();
      if (!isValidData(terms)) return;
      try {
        await axiosInstance.put(
          `/SmartProAPI/api/terms-and-conditions/${id}`,
          terms,
        );
        showAlert('success', 'Terms updated successfully');
        showTermsScreen();
        clearFormData();
        getAllTermsAndConditions();
      } catch (error) {
        setError(error);
        handleApiError('error', error.response?.data, error?.response?.status);
      } finally {
        stopLoading();
      }
    },
    [getAllTermsAndConditions, isValidData, startLoading, stopLoading],
  );

  const clearFormData = useCallback(() => {
    setError(null);
    setTerms(initialState);
  }, [initialState, setTerms]);

  return (
    <TermsAndConditionContext.Provider
      value={{
        getAllTermsAndConditions,
        termsData,
        setTermsData,
        error,
        setError,
        terms,
        setTerms,
        deleteTermsAndConditions,
        createNewTermsAndConditions,
        updateTermsAndConditions,
        clearFormData,
      }}
    >
      {children}
    </TermsAndConditionContext.Provider>
  );
};

export default TermsAndConditionContextProvider;
export const useTermsAndConditions = () => useContext(TermsAndConditionContext);
